/**
 * Offline queue for Helper-only capture flows (Seller / Purchaser / Animal /
 * Vehicle / Cash / Photo).  Uses IndexedDB via idb-keyval.  Lightweight, no
 * schema changes on the backend — replays go through the SAME
 * `/api/helper/registration` endpoint with an added `client_id` for
 * idempotency (backend returns `duplicate:true` if the record was already
 * synced from a prior device attempt).
 *
 * NEVER used for Login / Subscription / Admin Dashboard / Reports / User Mgmt.
 */
import { get as idbGet, del as idbDel } from "idb-keyval";
import { secureGet, secureSet } from "@/lib/secureStore";
import { api, getUser } from "@/lib/api";

const QUEUE_KEY = "am_offline_queue_v1";
const listeners = new Set();

const emit = () => listeners.forEach((fn) => { try { fn(); } catch (e) { console.debug(e); } });
export function subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); }

/** Cryptographically-random-ish offline id — good enough for dedupe. */
function newOfflineId() {
  const s = () => Math.random().toString(36).slice(2, 10);
  return `off-${Date.now().toString(36)}-${s()}${s()}`;
}

async function readQueue() {
  const enc = await secureGet(QUEUE_KEY);
  if (Array.isArray(enc)) return enc;
  // One-time migration from the legacy plaintext queue.
  const legacy = await idbGet(QUEUE_KEY);
  if (Array.isArray(legacy) && legacy.length) {
    await idbDel(QUEUE_KEY);
    await secureSet(QUEUE_KEY, legacy);
    return legacy;
  }
  return [];
}
async function writeQueue(items) {
  await secureSet(QUEUE_KEY, items);
  emit();
}

/** Enqueue a registration payload for later sync. Never throws. */
export async function enqueue(payload, meta = {}) {
  const items = await readQueue();
  const item = {
    client_id: newOfflineId(),
    client_created_at: new Date().toISOString(),
    sync_status: "pending",           // pending | syncing | synced | failed
    attempts: 0,
    last_error: null,
    created_by: meta.created_by || null,
    entity: meta.entity || "registration",
    payload: { ...payload, client_id: undefined },  // will be re-added below
  };
  item.payload.client_id = item.client_id;
  item.payload.client_created_at = item.client_created_at;
  items.push(item);
  await writeQueue(items);
  return item;
}

export async function list() {
  return readQueue();
}

/** Enqueue a non-registration action (e.g. QR gate scan, photo attach) for replay when online. */
export async function enqueueAction(url, params, meta = {}) {
  const items = await readQueue();
  const item = {
    client_id: newOfflineId(),
    client_created_at: new Date().toISOString(),
    sync_status: "pending",
    attempts: 0,
    last_error: null,
    created_by: meta.created_by || null,
    entity: meta.entity || "action",
    action: { url, params, body: meta.body ?? null },
    payload: {},
  };
  items.push(item);
  await writeQueue(items);
  return item;
}

export async function counts() {
  const q = await readQueue();
  return {
    total: q.length,
    pending: q.filter((r) => r.sync_status === "pending" || r.sync_status === "failed").length,
    syncing: q.filter((r) => r.sync_status === "syncing").length,
    synced: q.filter((r) => r.sync_status === "synced").length,
    failed: q.filter((r) => r.sync_status === "failed").length,
    dead: q.filter((r) => r.sync_status === "dead").length,
  };
}

/** Purge already-synced records older than N days to keep storage light. */
export async function prune(daysToKeep = 3) {
  const cutoff = Date.now() - daysToKeep * 24 * 3600 * 1000;
  const q = await readQueue();
  const keep = q.filter((r) => r.sync_status !== "synced" || Date.parse(r.client_created_at) >= cutoff);
  if (keep.length !== q.length) await writeQueue(keep);
}

let syncing = false;

const MAX_ATTEMPTS = 5;   // move to `dead` state after this many failures

/** Best-effort queue-state report so Admin/Owner device monitoring shows sync health. */
async function heartbeat() {
  try {
    const items = await readQueue();
    const count = (s) => items.filter((it) => it.sync_status === s).length;
    await api.post("/sync/heartbeat", {
      pending: count("pending") + count("syncing"),
      failed: count("failed") + count("dead"),
      synced: count("synced"),
      last_error: items.find((it) => it.last_error)?.last_error || null,
    });
  } catch (e) { console.debug("sync heartbeat failed", e); }
}

/** Attempt to sync every pending/failed record. Safe to call any time. */
export async function syncAll({ force = false } = {}) {
  if (syncing) return { alreadyRunning: true };
  syncing = true;
  try {
    const items = await readQueue();
    if (items.length === 0) return { synced: 0 };
    let synced = 0;
    for (const item of items) {
      if (item.sync_status === "synced" || item.sync_status === "dead") continue;
      if (!force && item.sync_status === "syncing") continue;
      item.sync_status = "syncing";
      item.attempts = (item.attempts || 0) + 1;
      await writeQueue(items);
      try {
        const r = item.action
          ? await api.post(item.action.url, item.action.body ?? null, { params: item.action.params })
          : await api.post("/helper/registration", item.payload);
        item.sync_status = "synced";
        item.synced_at = new Date().toISOString();
        item.server_reg_no = r.data?.animal?.registration_number || null;
        item.last_error = null;
        synced += 1;
      } catch (err) {
        item.last_error = err?.response?.data?.detail || err?.message || "unknown";
        // Non-network validation errors move straight to dead once past MAX_ATTEMPTS.
        item.sync_status = item.attempts >= MAX_ATTEMPTS ? "dead" : "failed";
      }
      await writeQueue(items);
    }
    await prune();
    await heartbeat();
    return { synced };
  } finally {
    syncing = false;
    emit();
  }
}

/** Retry only the failed rows (does not touch synced / dead ones). */
export async function retryFailed() {
  const items = await readQueue();
  let mutated = false;
  for (const it of items) {
    if (it.sync_status === "failed") { it.sync_status = "pending"; mutated = true; }
  }
  if (mutated) await writeQueue(items);
  return syncAll();
}

/** Discard a single record (e.g., a dead-letter that will never succeed). */
export async function discard(clientId) {
  const items = await readQueue();
  const keep = items.filter((it) => it.client_id !== clientId);
  if (keep.length !== items.length) await writeQueue(keep);
}

/** Prefetch the role-relevant GET endpoints so they are available offline. */
const WARM_URLS = {
  helper: ["/helper/animals", "/helper/gatepasses", "/helper/sellers", "/helper/purchasers", "/helper/cash", "/helper/registration-prices", "/helper/receipt-context", "/master/animals"],
  subscriber: ["/subscriber/dashboard", "/subscriber/profile", "/subscriber/helpers-with-email", "/subscriber/animals",
               "/subscriber/collections", "/subscriber/reports/daily", "/subscriber/reports/helper-collection", "/subscriber/devices",
               "/master/animals"],
  super_admin: ["/admin/stats", "/admin/subscribers", "/admin/devices", "/master/animals"],
};

export function warmOfflineCache() {
  if (!navigator.onLine) return;
  const role = getUser()?.role;
  (WARM_URLS[role] || []).forEach((u) => api.get(u).catch(() => {}));
}

/** Wire up window/native online events so pending work syncs automatically. */
export function startAutoSync() {
  const trigger = () => {
    if (!navigator.onLine) return;
    syncAll().catch(() => {});
    warmOfflineCache();
  };
  window.addEventListener("online", trigger);
  // Background auto-sync sweep — keeps queue + caches fresh while the app is open.
  const timer = setInterval(trigger, 60_000);
  // First-load sweep — attempts to flush whatever survived a refresh.
  setTimeout(trigger, 800);
  return () => { window.removeEventListener("online", trigger); clearInterval(timer); };
}
